// The tracking number a form shows from its first step (05 New shipment, 05 §5.2 pickup stop).
//
// The number is this device's next leased number, read without using it (`peekNumber`). It is
// read again whenever the device's count for the series changes (a shipment saved in this tab, a
// new range leased), when the connection comes back, and when the screen asks (`refresh`, for
// example when the window gets the focus back after another tab may have used it). The save then
// uses exactly the number shown (`takeShownNumber`). When the next number is no longer the one the
// person saw, the state says so (`changedFrom`) until they accept the new one: never silently.
import type { Connectivity } from './connectivity';
import { leaseCount, NoNumbersLeftError, type LeaseManager, type NumberSeries } from './leases';
import { createStore, type ReadableStore } from './store';

export type UpfrontNumberState =
  /** Reading the device's ranges, or asking the server for a first range. */
  | { status: 'loading' }
  /**
   * `changedFrom`: the number the person was shown before, when this device's next number is no
   * longer it (another tab or form on this device used it). The form says so and waits for
   * `accept` before it saves, so a number never changes silently.
   */
  | { status: 'ready'; number: string; changedFrom?: string }
  /** None on this device and no connection to ask for more. `message` is safe to show. */
  | { status: 'offline_empty'; message: string }
  /** The device asked the server for numbers and it failed. `message` is safe to show. */
  | { status: 'error'; message: string };

export interface UpfrontNumber extends ReadableStore<UpfrontNumberState> {
  /** Read the next number again (keeps showing the current one meanwhile). */
  refresh(): void;
  /** After an error: show the spinner and ask again. */
  retry(): void;
  /** Show this number now (the save found that the next number is another one). */
  show(number: string): void;
  /** The person has seen the new number: clear `changedFrom`. */
  accept(): void;
  /** This form saved with `number`: the next number shown is a fresh one, not a change. */
  markUsed(number: string): void;
}

export interface UpfrontNumberOptions {
  leases: Pick<LeaseManager, 'peekNumber' | 'subscribe' | 'getSnapshot'>;
  connectivity: Pick<Connectivity, 'subscribe' | 'getSnapshot'>;
  series: NumberSeries;
  branchId: string;
  /** A tracking numbering series other than the branch default (03 §3.5). */
  seriesId?: string;
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Watches the next number of a series at a branch. Nothing happens until the first subscriber
 * (React's `useSyncExternalStore`), and it stops when the last one leaves, so creating one is free.
 * While watched it also reads again when the window gets the focus back: another tab of this
 * browser shares the device's ranges and may have used the number shown here.
 */
export function watchUpfrontNumber(options: UpfrontNumberOptions): UpfrontNumber {
  const { leases, connectivity, series, branchId, seriesId } = options;
  const state = createStore<UpfrontNumberState>({ status: 'loading' });
  let round = 0;
  let watchers = 0;
  let halt: (() => void) | null = null;
  /** The number the person has been shown and accepted. */
  let accepted: string | null = null;
  /** The number this form last saved with. */
  let used: string | null = null;

  const publish = (number: string): void => {
    const now = state.getSnapshot();
    if (number === used) {
      // An answer from before the save: the next one is on its way.
      if (now.status !== 'loading') state.set({ status: 'loading' });
      return;
    }
    if (accepted === null || accepted === used) accepted = number;
    const changedFrom = accepted === number ? undefined : accepted;
    if (now.status === 'ready' && now.number === number && now.changedFrom === changedFrom) return;
    state.set(changedFrom ? { status: 'ready', number, changedFrom } : { status: 'ready', number });
  };

  const read = (): void => {
    const mine = (round += 1);
    leases.peekNumber(series, branchId, seriesId).then(
      (number) => {
        if (mine !== round) return;
        publish(number);
      },
      (err: unknown) => {
        if (mine !== round) return;
        const offline = err instanceof NoNumbersLeftError ? err.offline : false;
        const message = messageOf(err);
        const now = state.getSnapshot();
        if (now.status !== 'ready' && 'message' in now && now.message === message) return;
        state.set(offline ? { status: 'offline_empty', message } : { status: 'error', message });
      },
    );
  };

  const start = (): (() => void) => {
    // Read again when this device's count for the series changes. Only the count: a failed lease
    // request also changes the summary, and reacting to that would ask the server again in a loop.
    let count = leaseCount(leases.getSnapshot(), series, branchId, seriesId);
    const stopLeases = leases.subscribe(() => {
      const n = leaseCount(leases.getSnapshot(), series, branchId, seriesId);
      if (n === count) return;
      count = n;
      read();
    });
    // Back online: a device with no numbers can lease one now, and an error is worth another try.
    let online = connectivity.getSnapshot().online;
    const stopConnectivity = connectivity.subscribe(() => {
      const now = connectivity.getSnapshot().online;
      if (now === online) return;
      online = now;
      if (now) read();
    });
    const onFocus = (): void => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') read();
    };
    if (typeof window !== 'undefined') window.addEventListener('focus', onFocus);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onFocus);
    read();
    return () => {
      round += 1; // an answer still on its way is no longer wanted
      stopLeases();
      stopConnectivity();
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
      if (typeof document !== 'undefined')
        document.removeEventListener('visibilitychange', onFocus);
    };
  };

  return {
    subscribe(listener) {
      const off = state.subscribe(listener);
      watchers += 1;
      if (watchers === 1) halt = start();
      return () => {
        off();
        watchers -= 1;
        if (watchers === 0 && halt) {
          halt();
          halt = null;
        }
      };
    },
    getSnapshot: state.getSnapshot,
    refresh: read,
    retry() {
      state.set({ status: 'loading' });
      read();
    },
    show(number) {
      round += 1;
      publish(number);
    },
    accept() {
      const now = state.getSnapshot();
      if (now.status !== 'ready') return;
      accepted = now.number;
      if (now.changedFrom !== undefined) state.set({ status: 'ready', number: now.number });
    },
    markUsed(number) {
      used = number;
      const now = state.getSnapshot();
      if (now.status === 'ready' && now.number !== number) {
        accepted = now.number;
        state.set({ status: 'ready', number: now.number });
        return;
      }
      accepted = number;
      state.set({ status: 'loading' });
      if (watchers > 0) read();
    },
  };
}
